import { create } from 'zustand';
import { User, AuthTokens } from '@/types/user';
import { websocketService } from '@/services/websocketService';
import { projectService } from '@/services/projectService';
import { useProjectStore } from './projectStore';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: User) => void;
  setTokens: (tokens: AuthTokens) => void;
  setAccessToken: (token: string) => void;
  login: (user: User, tokens: AuthTokens) => void;
  logout: () => void;
  initialize: () => void;
}

const STORAGE_KEYS = {
  USER: 'auth_user',
  ACCESS_TOKEN: 'auth_access_token',
  REFRESH_TOKEN: 'auth_refresh_token',
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,

  setUser: (user) => set({ user }),

  setTokens: (tokens) =>
    set({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
    }),

  setAccessToken: (token) => {
    set({ accessToken: token });
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  },

  login: async (user, tokens) => {
    // Save to localStorage
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);

    // Update state
    set({
      user,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      isAuthenticated: true,
      isLoading: false,
    });

    // Connect WebSocket - TEMPORARILY DISABLED (WebSocket not implemented on backend)
    // websocketService.connect();

    // Initialize default project from preferences, then SUP key, then first
    try {
      const projects = await projectService.getProjects();
      if (projects && projects.length > 0) {
        const prefDefaultId = (user as any)?.preferences?.default_project_id;
        const defaultProject =
          (prefDefaultId && projects.find(p => p.id === prefDefaultId)) ||
          projects.find(p => p.key === 'SUP') ||
          projects[0];
        useProjectStore.getState().setCurrentProject(defaultProject);
      }
    } catch (error) {
      console.error('Failed to load default project:', error);
    }
  },

  logout: () => {
    // Clear localStorage
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

    // Update state
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
    });

    // Clear current project
    useProjectStore.getState().clearProject();

    // Disconnect WebSocket - TEMPORARILY DISABLED (WebSocket not implemented on backend)
    // websocketService.disconnect();
  },

  initialize: async () => {
    try {
      // Load from localStorage
      const userStr = localStorage.getItem(STORAGE_KEYS.USER);
      const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

      if (userStr && accessToken && refreshToken) {
        const user = JSON.parse(userStr) as User;

        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });

        // Connect WebSocket - TEMPORARILY DISABLED (WebSocket not implemented on backend)
        // websocketService.connect();

        // Initialize default project if not already set
        const currentProject = useProjectStore.getState().currentProject;
        if (!currentProject) {
          try {
            const projects = await projectService.getProjects();
            if (projects && projects.length > 0) {
              const prefDefaultId = (user as any)?.preferences?.default_project_id;
              const defaultProject =
                (prefDefaultId && projects.find(p => p.id === prefDefaultId)) ||
                projects.find(p => p.key === 'SUP') ||
                projects[0];
              useProjectStore.getState().setCurrentProject(defaultProject);
            }
          } catch (error) {
            console.error('Failed to load default project:', error);
          }
        }
      } else {
        set({ isLoading: false });
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      set({ isLoading: false });
    }
  },
}));
